import Loader from 'components/Loader';
import usePythonFunctions from 'api/python-functions/usePythonFunctions';
import FolderTree from 'components/folders/FolderTree';
import PythonFunctionItem from './PythonFunctionItem';
import { useState } from 'react';
import PythonFunctionAdd from './PythonFunctionAdd';
import PythonFunctionEdit from './PythonFunctionEdit';
import { useParams } from 'react-router';
import { useSearchParams } from 'react-router-dom';
import { useKeyboard } from 'hooks/useKeyboard';
import { SettingsSection } from 'components/settingsHub/SettingsSection';
import { Scope } from 'components/settingsHub/ScopeFilter';
import { AddEntityCard } from 'components/settingsHub/EntityCard';
import { ScopeButton, ScopeGroup } from 'components/settingsHub/settingsEntities.styles';
import { SettingsDetailPanel } from 'components/settingsHub/SettingsDetailPanel';

type Props = {
  projectId?: string | null;
};

const PythonFunctionsView = ({ projectId = null }: Props) => {
  const { projectId: routeProjectId, workflowId } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const actualProjectId = projectId ?? routeProjectId;
  const [scope, setScope] = useState<Scope>(actualProjectId ? 'project' : 'global');
  const [search, setSearch] = useState('');
  const [unusedOnly, setUnusedOnly] = useState(false);
  const includeGlobal = scope === 'all' || scope === 'global';

  const { functions, isLoading } = usePythonFunctions(actualProjectId, includeGlobal, unusedOnly);
  const [addForm, setAddForm] = useState<{ folderPath: string } | null>(null);

  const setSelectedFunction = (functionId: string | null) => {
    setSearchParams((params) => {
      if (!functionId) {
        params.delete('functionId');
        params.delete('revision');
      } else {
        params.set('functionId', functionId);
      }
      return params;
    });
  };

  useKeyboard({
    keyDown: (e) => {
      if (e.key === 'Escape' && searchParams.get('config')) {
        if (searchParams.get('functionId')) {
          setSelectedFunction(null);
        } else if (addForm) {
          setAddForm(null);
        } else {
          setSearchParams({});
        }
      }
    },
  });

  if (isLoading) return <Loader />;

  const selectedFunctionId = searchParams.get('functionId');
  const query = search.trim().toLowerCase();
  const visibleFunctions = [...(functions ?? [])]
    .filter((fn) => !query || fn.name.toLowerCase().includes(query))
    .filter((fn) => {
      if (scope === 'project') return fn.project_id && fn.project_id === actualProjectId;
      if (scope === 'global') return !fn.project_id;
      return true;
    })
    .sort((a, b) => b.created_at.localeCompare(a.created_at));

  return (
    <>
      <SettingsSection
        showScope={!!actualProjectId}
        scope={scope}
        onScopeChange={setScope}
        search={search}
        onSearchChange={setSearch}
        searchPlaceholder="Search functions…"
        count={visibleFunctions.length}
        countNoun="functions"
        filters={
          <ScopeGroup>
            <ScopeButton
              type="button"
              selected={unusedOnly}
              aria-pressed={unusedOnly}
              onClick={() => setUnusedOnly((prev) => !prev)}
            >
              Unused
            </ScopeButton>
          </ScopeGroup>
        }
      >
        <FolderTree
          items={visibleFunctions}
          getKey={(fn) => fn.id}
          getFolderPath={(fn) => fn.folder_path}
          renderItem={(fn) => <PythonFunctionItem pythonFunction={fn} onSelect={() => setSelectedFunction(fn.id)} />}
          forceExpand={!!query}
          rootLabel="All functions"
          itemName="function"
          renderAdd={(folderPath) => (
            <AddEntityCard label="Add New Function" onClick={() => setAddForm({ folderPath })} minHeight={208} />
          )}
        />
      </SettingsSection>

      {addForm && (
        <SettingsDetailPanel onClose={() => setAddForm(null)}>
          <PythonFunctionAdd
            projectId={actualProjectId ?? ''}
            initialFolderPath={addForm.folderPath}
            onCancel={() => setAddForm(null)}
            onSubmit={() => setAddForm(null)}
          />
        </SettingsDetailPanel>
      )}

      {selectedFunctionId && (
        <SettingsDetailPanel onClose={() => setSelectedFunction(null)}>
          <PythonFunctionEdit
            functionId={selectedFunctionId}
            projectId={actualProjectId ?? ''}
            activeWorkflowId={workflowId}
            onCancel={() => setSelectedFunction(null)}
            onSubmit={() => setSelectedFunction(null)}
          />
        </SettingsDetailPanel>
      )}
    </>
  );
};

export default PythonFunctionsView;
